import React, { useContext, useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import {
  LayoutDashboard,
  Repeat,
  MessageCircle,
  Contact,
  Network,
  MessagesSquare,
  Users,
  Settings,
  Tag,
  Megaphone,
  UserPlus,
  Filter,
  CalendarClock,
  Image,
  Users2,
  Building2,
  Webhook,
  KanbanSquare,
  MessageSquareText,
  Workflow,
  BarChart3,
  ChevronDown,
  Megaphone as Aviso,
  ListChecks,
  Building,
  Receipt,
  Package,
  Puzzle,
  Shield,
  Sparkles,
  Activity,
  CircleDollarSign,
  BadgePercent,
  Bot,
  Lock,
  LifeBuoy,
  Plug,
  SlidersHorizontal,
  Quote,
  // Share2, // ícone do item Facebook e Instagram, hoje fora do menu
} from "lucide-react";

import { cn } from "../lib/utils";
import { lerPreferencia, gravarPreferencia } from "../services/preferencias";
import { i18n } from "../translate/i18n";
import { WhatsAppsContext } from "../context/WhatsApp/WhatsAppsContext";
import { AuthContext } from "../context/Auth/AuthContext";
import useModules from "../hooks/useModules";
import { Can } from "../components/Can";

function NavItem({
  to,
  icon: Icon,
  label,
  badge,
  onClick,
  collapsed,
  // Recurso fora do plano. Some do menu era pior do que aparecer apagado:
  // quem não vê o item não sabe que ele existe, e não tem o que pedir.
  foraDoPlano,
}) {
  return (
    <NavLink
      to={to}
      onClick={onClick}
      title={
        foraDoPlano
          ? `${label} — não está no seu plano. Fale com quem administra a conta para liberar.`
          : undefined
      }
      className={({ isActive }) =>
        cn(
          "group flex items-center gap-3 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors",
          "hover:bg-accent hover:text-accent-foreground",
          isActive ? "bg-primary/10 text-primary" : "text-muted-foreground",
          foraDoPlano && "opacity-50"
        )
      }
    >
      <span className="relative flex h-5 w-5 shrink-0 items-center justify-center">
        <Icon className="h-[18px] w-[18px]" />
        {badge && (
          <span className="absolute -right-1 -top-1 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-destructive text-[9px] font-bold text-destructive-foreground">
            !
          </span>
        )}
      </span>
      {!collapsed && (
        <>
          <span className="truncate">{label}</span>
          {foraDoPlano && <Lock className="ml-auto h-3 w-3 shrink-0" />}
        </>
      )}
    </NavLink>
  );
}

function NavSection({ id, label, collapsed, fechadaPorPadrao, children }) {
  // O "v2" zera a preferência guardada uma vez. Configurações nascia fechada e
  // engolia Conexões, Filas e Usuários; quem já tinha o "fechado" salvo
  // continuaria sem ver mesmo depois da mudança. Depois deste reset, a escolha
  // de cada um volta a valer normalmente.
  const storageKey = `navSection:v2:${id}`;
  const [open, setOpen] = useState(() => {
    const stored = lerPreferencia(storageKey, null);
    // Hoje só a Gestão SaaS nasce fechada: são telas de dono do produto, não
    // do dia a dia de quem atende.
    if (stored === null) return !fechadaPorPadrao;
    return stored === true;
  });

  const toggle = (e) => {
    // não deixa o clique do cabeçalho fechar o drawer mobile
    e.stopPropagation();
    setOpen((prev) => {
      gravarPreferencia(storageKey, !prev);
      return !prev;
    });
  };

  if (collapsed) {
    return (
      <>
        <div className="my-2 h-px bg-border" />
        {children}
      </>
    );
  }

  return (
    <div className="mt-3">
      <button
        type="button"
        onClick={toggle}
        className="flex w-full items-center justify-between rounded-md px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/70 transition-colors hover:text-foreground"
      >
        <span>{label}</span>
        <ChevronDown
          className={cn(
            "h-3.5 w-3.5 transition-transform duration-200",
            !open && "-rotate-90"
          )}
        />
      </button>
      {/* Recuo com guia vertical: mostra de relance onde um grupo começa e
          termina. Sem isso, título e itens ficavam no mesmo alinhamento e a
          lista parecia uma coluna única de 36 linhas soltas. */}
      <div
        className={cn(
          "ml-[18px] mt-0.5 flex flex-col gap-0.5 overflow-hidden border-l border-border/60 pl-1.5 transition-all",
          !open && "hidden"
        )}
      >
        {children}
      </div>
    </div>
  );
}

const MainListItems = ({ drawerClose, collapsed }) => {
  const { whatsApps } = useContext(WhatsAppsContext);
  const { user } = useContext(AuthContext);
  // Esconde do menu o que a empresa nao contratou.
  const { temModulo } = useModules();
  const [connectionWarning, setConnectionWarning] = useState(false);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (whatsApps.length > 0) {
        const offlineWhats = whatsApps.filter((whats) => {
          return (
            whats.status === "qrcode" ||
            whats.status === "PAIRING" ||
            whats.status === "DISCONNECTED" ||
            whats.status === "TIMEOUT" ||
            whats.status === "OPENING"
          );
        });
        setConnectionWarning(offlineWhats.length > 0);
      }
    }, 2000);
    return () => clearTimeout(delayDebounceFn);
  }, [whatsApps]);

  return (
    <nav className="flex flex-col gap-0.5 px-2 pb-6" onClick={drawerClose}>
      {/* Dashboard sozinho no topo: é a tela inicial, não pertence a
          nenhum grupo. */}
      <NavItem
        to="/"
        icon={LayoutDashboard}
        label="Dashboard"
        collapsed={collapsed}
      />

      {/* Atendimento: a conversa, o contato e o material que a equipe reusa
          ao responder. */}
      <NavSection id="atendimento" label="Atendimento" collapsed={collapsed}>
        <NavItem
          to="/tickets"
          icon={MessageCircle}
          label={i18n.t("mainDrawer.listItems.tickets")}
          collapsed={collapsed}
        />
        <NavItem
          to="/contacts"
          icon={Contact}
          label={i18n.t("mainDrawer.listItems.contacts")}
          collapsed={collapsed}
        />
        <NavItem
          to="/quickAnswers"
          icon={MessagesSquare}
          label={i18n.t("mainDrawer.listItems.quickAnswers")}
          collapsed={collapsed}
        />
        <NavItem
            to="/media-library"
            icon={Image}
            label="Biblioteca de mídias"
            collapsed={collapsed}
            foraDoPlano={!temModulo("mediaLibrary")}
          />
      </NavSection>

      {/* Automação: tudo que fala com o cliente sem ninguém digitar.
          As mensagens agendadas vieram para cá — elas são envio automático,
          não material de apoio. */}
      <NavSection id="automacao" label="Automação" collapsed={collapsed}>
        <Can
          user={user}
          perform="automation:view"
          yes={() => (
            <NavItem
              to="/flows"
              icon={Workflow}
              label="Fluxos"
              collapsed={collapsed}
              foraDoPlano={!temModulo("flows")}
            />
          )}
        />
        <NavItem
            to="/scheduled-messages"
            icon={CalendarClock}
            label="Mensagens agendadas"
            collapsed={collapsed}
            foraDoPlano={!temModulo("schedules")}
          />
        <Can
          user={user}
          perform="ai:use"
          yes={() => (
            <NavItem
              to="/ai"
              icon={Sparkles}
              label="Talk.Ai"
              collapsed={collapsed}
            />
          )}
        />
        {/* Facebook e Instagram fora do menu por enquanto: o token da Meta
            expirou e o app ainda não tem o App Secret para renovar. A tela e
            a integração continuam no código — é só devolver este NavItem e a
            rota em routes/index.js quando as credenciais estiverem prontas. */}
      </NavSection>

      {/* Vender: prospecção e disparo em massa moram juntos porque a campanha
          é a ferramenta de quem trabalha os leads. */}
      <NavSection id="vendas" label="Vendas" collapsed={collapsed}>
        <NavItem
            to="/crm/leads"
            icon={UserPlus}
            label="Leads"
            collapsed={collapsed}
            foraDoPlano={!temModulo("crm")}
          />
        <NavItem
            to="/crm/funnel"
            icon={Filter}
            label="Funil de vendas"
            collapsed={collapsed}
            foraDoPlano={!temModulo("crm")}
          />
        <NavItem
            to="/campaigns"
            icon={Megaphone}
            label="Campanhas"
            collapsed={collapsed}
            foraDoPlano={!temModulo("campaigns")}
          />
        <NavItem
            to="/contact-lists"
            icon={ListChecks}
            label="Listas de contatos"
            collapsed={collapsed}
            foraDoPlano={!temModulo("campaigns")}
          />
        <NavItem
          to="/campaigns-phrases"
          icon={Quote}
          label="Listas de frases"
          collapsed={collapsed}
          foraDoPlano={!temModulo("campaigns")}
        />
        <NavItem
          to="/campaigns-config"
          icon={SlidersHorizontal}
          label="Config. de campanhas"
          collapsed={collapsed}
          foraDoPlano={!temModulo("campaigns")}
        />
      </NavSection>

      {/* Acompanhar: os números do que já aconteceu. */}
      <NavSection id="analise" label="Acompanhamento" collapsed={collapsed}>
        <NavItem
          to="/operational-panel"
          icon={Activity}
          label="Painel operacional"
          collapsed={collapsed}
        />
        <NavItem
            to="/reports"
            icon={BarChart3}
            label="Relatórios"
            collapsed={collapsed}
            foraDoPlano={!temModulo("reports")}
          />
      </NavSection>

      {/* Equipe: conversa interna, não com o cliente. */}
      <NavSection id="equipe" label="Equipe" collapsed={collapsed}>
        <NavItem
            to="/internal-chat"
            icon={MessageSquareText}
            label="Chat interno"
            collapsed={collapsed}
            foraDoPlano={!temModulo("internalChat")}
          />
        <NavItem
          to="/announcements"
          icon={Aviso}
          label="Avisos internos"
          collapsed={collapsed}
        />
        <NavItem
          to="/helps"
          icon={LifeBuoy}
          label="Ajuda"
          collapsed={collapsed}
        />
        <NavItem
            to="/boards"
            icon={KanbanSquare}
            label="Quadros"
            collapsed={collapsed}
            foraDoPlano={!temModulo("kanban")}
          />
      </NavSection>

      {/* Configurações: o que se ajusta uma vez e esquece. Nasce fechada —
          são nove linhas que empurravam o dia a dia para fora da tela.
          Conexões saiu do topo: é configuração, mesmo sendo importante. */}
      <Can
        user={user}
        perform="drawer-admin-items:view"
        yes={() => (
          <NavSection
            id="configuracoes"
            label="Configurações"
            collapsed={collapsed}
          >
            <NavItem
              to="/connections"
              icon={Repeat}
              label={i18n.t("mainDrawer.listItems.connections")}
              badge={connectionWarning}
              collapsed={collapsed}
            />
            <NavItem
              to="/Queues"
              icon={Network}
              label={i18n.t("mainDrawer.listItems.queues")}
              collapsed={collapsed}
            />
            <NavItem
              to="/tags"
              icon={Tag}
              label={i18n.t("mainDrawer.listItems.tags")}
              collapsed={collapsed}
            />
            <NavItem
              to="/users"
              icon={Users}
              label={i18n.t("mainDrawer.listItems.users")}
              collapsed={collapsed}
            />
            <NavItem
              to="/roles"
              icon={Shield}
              label="Cargos e permissões"
              collapsed={collapsed}
            />
            <NavItem
              to="/teams"
              icon={Users2}
              label="Equipes"
              collapsed={collapsed}
            />
            <NavItem
              to="/departments"
              icon={Building2}
              label="Departamentos"
              collapsed={collapsed}
            />
            <NavItem
                to="/webhooks"
                icon={Webhook}
                label="Webhooks"
                collapsed={collapsed}
                foraDoPlano={!temModulo("webhooks")}
              />
            <NavItem
              to="/queue-integrations"
              icon={Bot}
              label="Integrações"
              collapsed={collapsed}
            />
            <NavItem
              to="/messages-api"
              icon={Plug}
              label="API"
              collapsed={collapsed}
            />
            {/* Ajustes da plataforma inteira: a rota é isSuperAdmin, então
                mostrar para o cliente só renderia um 403. */}
            {Number(user.companyId) === 1 && (
              <NavItem
                to="/global-config"
                icon={SlidersHorizontal}
                label="Configurações globais"
                collapsed={collapsed}
              />
            )}
            {/* A empresa operadora já tem o painel financeiro do SaaS; para
                ela esta tela seria a própria fatura, que não existe. */}
            {Number(user.companyId) !== 1 && (
              <NavItem
                to="/financeiro"
                icon={CircleDollarSign}
                label="Financeiro"
                collapsed={collapsed}
              />
            )}
            <NavItem
              to="/Settings"
              icon={Settings}
              label={i18n.t("mainDrawer.listItems.settings")}
              collapsed={collapsed}
            />
          </NavSection>
        )}
      />

      {/* Gestão do SaaS: só o admin da empresa operadora (id 1) enxerga. */}
      {user.profile === "admin" && Number(user.companyId) === 1 && (
        <NavSection
          id="saas"
          label="Gestão SaaS"
          collapsed={collapsed}
          fechadaPorPadrao
        >
          <NavItem
            to="/saas/finance"
            icon={CircleDollarSign}
            label="Painel financeiro"
            collapsed={collapsed}
          />
          <NavItem
            to="/companies"
            icon={Building}
            label="Empresas"
            collapsed={collapsed}
          />
          <NavItem
            to="/plans"
            icon={Package}
            label="Planos"
            collapsed={collapsed}
          />
          <NavItem
            to="/modules"
            icon={Puzzle}
            label="Módulos"
            collapsed={collapsed}
          />
          <NavItem
            to="/saas/commercial"
            icon={BadgePercent}
            label="Comercial"
            collapsed={collapsed}
          />
          <NavItem
            to="/invoices"
            icon={Receipt}
            label="Faturas"
            collapsed={collapsed}
          />
        </NavSection>
      )}
    </nav>
  );
};

export default MainListItems;
